/**
 * Exploded-view geometry, expressed as pure functions on plain tuples.
 *
 * This module is deliberately free of any 3D library: it is the part of the exploded view that
 * carries the rules (radial default, manifest override, fixed parts, interpolation) and it must
 * stay unit-testable without a GL context. The viewer is responsible for the two things that do
 * need a 3D library: computing bounding boxes, and converting the world-space offset returned
 * here into the local space of a node's parent.
 */

import {Vec3} from '@shared/models/cad_model';

/**
 * Fraction of the assembly's bounding-box diagonal a part travels at 100 % explosion, for an
 * `explodeDistance` of 1.
 *
 * Tuned on the MaviRoll Connect assembly: much above this and a re-framed exploded view shrinks the
 * parts until they are hard to identify, which defeats the point of the view.
 */
export const EXPLODE_BASE_FACTOR = 0.22;

/** Direction used when no meaningful direction can be derived (part centred on the assembly). */
export const EXPLODE_FALLBACK_DIRECTION: Vec3 = [0, 1, 0];

/** The subset of a part the explode maths needs. */
export interface ExplodePart {
  /** World-space centre of the part's bounding box, in the assembled state. */
  center: Vec3;
  fixed?: boolean;
  /** Manifest override. Takes precedence over the radial direction. Needs not be normalised. */
  explodeVector?: Vec3;
  /** Multiplier on the travelled distance. Defaults to 1. */
  explodeDistance?: number;
}

/** The subset of the whole assembly the explode maths needs. */
export interface ExplodeLayout {
  /** World-space centre of the whole assembly's bounding box. */
  center: Vec3;
  /** Length of the assembly's bounding-box diagonal; scales every offset. */
  diagonal: number;
}

export function subtract(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

export function add(a: Vec3, b: Vec3): Vec3 {
  return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
}

export function scale(v: Vec3, factor: number): Vec3 {
  return [v[0] * factor, v[1] * factor, v[2] * factor];
}

export function length(v: Vec3): number {
  return Math.hypot(v[0], v[1], v[2]);
}

/** Normalises `v`, or returns `undefined` when it is too short to carry a direction. */
export function normalize(v: Vec3): Vec3 | undefined {
  const len = length(v);
  if (!Number.isFinite(len) || len < Number.EPSILON) {
    return undefined;
  }
  return [v[0] / len, v[1] / len, v[2] / len];
}

export function lerp(a: Vec3, b: Vec3, t: number): Vec3 {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

/**
 * Clamps a slider ratio into 0..1.
 *
 * `NaN` collapses to 0 (assembled) because there is no sensible explosion for "no value", while
 * an infinite ratio saturates the way any large value would.
 */
export function clamp01(value: number): number {
  if (Number.isNaN(value)) {
    return 0;
  }
  return Math.min(1, Math.max(0, value));
}

/**
 * Normalised direction along which a part moves when the assembly explodes.
 *
 * `explodeVector` from the manifest wins, which is what makes internal, coaxial or symmetric
 * parts correctable by hand. Otherwise the direction is radial: from the assembly centre towards
 * the part centre. A part sitting exactly on the assembly centre has no radial direction, so a
 * deterministic fallback is used rather than leaving it stuck inside the assembly.
 */
export function computeExplodeDirection(part: ExplodePart, layout: ExplodeLayout): Vec3 {
  if (part.explodeVector !== undefined) {
    const overridden = normalize(part.explodeVector);
    if (overridden !== undefined) {
      return overridden;
    }
  }
  const radial = normalize(subtract(part.center, layout.center));
  return radial ?? EXPLODE_FALLBACK_DIRECTION;
}

/**
 * World-space offset to add to a part's assembled position.
 *
 * `ratio` is the slider value, 0 (assembled) to 1 (fully exploded). Parts flagged `fixed` never
 * move. The offset is always recomputed from the part's stored original position, which is what
 * prevents drift from accumulating across slider moves.
 */
export function computeExplodeOffset(
  part: ExplodePart,
  layout: ExplodeLayout,
  ratio: number
): Vec3 {
  if (part.fixed === true) {
    return [0, 0, 0];
  }
  const clamped = clamp01(ratio);
  if (clamped === 0) {
    return [0, 0, 0];
  }
  const diagonal = Number.isFinite(layout.diagonal) && layout.diagonal > 0 ? layout.diagonal : 0;
  if (diagonal === 0) {
    return [0, 0, 0];
  }
  const distance = part.explodeDistance ?? 1;
  if (!Number.isFinite(distance) || distance === 0) {
    return [0, 0, 0];
  }
  const direction = computeExplodeDirection(part, layout);
  return scale(direction, diagonal * EXPLODE_BASE_FACTOR * distance * clamped);
}

/**
 * Position of a part node at a given explosion ratio.
 *
 * `originalLocalPosition` is the node's untouched local translation, captured once after load.
 * `localOffset` is `computeExplodeOffset`'s world offset already rotated into the parent's local
 * space by the caller. Rotation and scale are never touched.
 */
export function applyExplodeOffset(originalLocalPosition: Vec3, localOffset: Vec3): Vec3 {
  return add(originalLocalPosition, localOffset);
}
